import React, { useEffect, useMemo, useState } from 'react';
import { Heading, Text, SimpleGrid, Box, Alert, AlertIcon, Container } from '@chakra-ui/react';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import MetricCard from '../../components/MetricCard';
import PieChart from '../../components/PieChart';
import TrendChart from '../../components/SampleBarChart';
import useAnalyticsAPI from '../../hooks/useAnalyticsAPI';

export default function AnalysisMetricsPage() {
  const { makeRequestWithFallback, loading, error } = useAnalyticsAPI();
  const [data, setData] = useState(null);

  const fetchDashboard = async () => {
    try {
      const result = await makeRequestWithFallback(
        '/api/bugs/analytics/dashboard',
        '/api/bugs/analytics'
      );
      
      setData(result);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rootCauseChart = useMemo(() => {
    const dist = data?.data?.topRootCauses || data?.topRootCauses || [];
    return Array.isArray(dist) ? dist.map(d => ({ 
      name: d.category || d.name || 'Unknown', 
      value: Number(d.count || d.value || 0) 
    })) : [];
  }, [data]);

  const severityChart = useMemo(() => {
    const sev = data?.data?.severityBreakdown || data?.severityBreakdown || [];
    return Array.isArray(sev) ? sev.map(s => ({ 
      name: s.severity || s.name || 'Unknown', 
      value: Number(s.count || s.value || 0) 
    })) : [];
  }, [data]);

  const trendSeries = useMemo(() => {
    const monthlyData = data?.data?.monthlyTrends || data?.monthlyTrends || [];
    const normalized = Array.isArray(monthlyData) ? monthlyData.map(r => ({ 
      period: r.month || r.period || r.date, 
      total: r.count || r.total || 0 
    })) : [];
    return { 
      data: normalized, 
      series: [{ key: 'total', label: 'Total Tickets', type: 'bar', color: 'blue.500' }] 
    };
  }, [data]);

  const metrics = useMemo(() => {
    const overview = data?.data?.overview || data?.overview || {};
    const severityData = data?.data?.severityBreakdown || data?.severityBreakdown || [];
    
    return {
      uniqueClients: overview.uniqueClients,
      affectedEmployees: overview.affectedEmployees,
      severityMediumPct: severityData.find(s => s.severity === 'MEDIUM')?.percentage,
      severityCriticalPct: severityData.find(s => s.severity === 'CRITICAL')?.percentage,
    };
  }, [data]);

  if (loading) return <LoadingState title="Loading analytics" minH="70vh" />;
  if (error) return <ErrorState message={error} />;

  return (
    <Page maxW="full">
      <Container maxW="8xl" p={0}>
        <Box mb={8}>
          <Heading size="xl" color="gray.600" mb={2}>Analysis Metrics Dashboard</Heading>
          <Text fontSize="lg" color="gray.500">
            Comprehensive metrics and performance analysis
          </Text>
        </Box>

      {error && (
        <Alert status="warning" mb={6}>
          <AlertIcon />
          {typeof error === 'string' ? error : error.message || 'An error occurred'}
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <MetricCard label="Unique Clients Affected" value={metrics.uniqueClients ?? '-'} color="blue.600" />
        <MetricCard label="Employees Impacted" value={metrics.affectedEmployees ?? '-'} color="orange.600" />
        <MetricCard label="Medium Severity" value={metrics.severityMediumPct != null ? `${metrics.severityMediumPct}%` : '-'} color="yellow.700" />
        <MetricCard label="Critical Severity" value={metrics.severityCriticalPct != null ? `${metrics.severityCriticalPct}%` : '-'} color="red.600" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
        <PieChart title="Ticket Root Cause Distribution" data={rootCauseChart} height={360} />
        <PieChart title="Severity Distribution" data={severityChart} height={360} />
      </SimpleGrid>

      <SectionCard mb={6}>
        <Box mb={3}><Text fontWeight="bold" color="gray.700">Monthly Trends</Text></Box>
        <TrendChart data={trendSeries.data} series={trendSeries.series} chartType="mixed" height={360} />
      </SectionCard>

      {/* AI Insights */}
      {data?.data?.aiInsights && (
        <SectionCard>
          <Heading size="md" mb={4}>AI Strategic Insights</Heading>
          {data.data.aiInsights.riskAssessment && (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
              <Box textAlign="center" p={4} borderRadius="lg" bg="blue.50">
                <Text fontSize="sm" color="gray.600">Overall Risk</Text>
                <Text fontSize="xl" fontWeight="bold" color="blue.600">
                  {data.data.aiInsights.riskAssessment.overallRiskLevel}
                </Text>
              </Box>
              <Box textAlign="center" p={4} borderRadius="lg" bg="orange.50">
                <Text fontSize="sm" color="gray.600">Risk Score</Text>
                <Text fontSize="xl" fontWeight="bold" color="orange.600">
                  {data.data.aiInsights.riskAssessment.riskScore}/100
                </Text>
              </Box>
              <Box textAlign="center" p={4} borderRadius="lg" bg="green.50">
                <Text fontSize="sm" color="gray.600">Confidence</Text>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  {data.data.aiInsights.riskAssessment.confidenceLevel}%
                </Text>
              </Box>
            </SimpleGrid>
          )}
          {data.data.aiInsights.summary && (
            <Text color="gray.700" lineHeight="1.6">
              {data.data.aiInsights.summary}
            </Text>
          )}
        </SectionCard>
      )}
      </Container>
    </Page>
  );
}


